import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Button, Space, Typography, Segmented, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faCommentSms,
  faCalendar,
  faLocationDot
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import caseService from '../../../services/CaseService';
import OutreachMembersTable from './OutreachMembersTable';
import { DrawerStatsBar, TableToolbar } from '../styled';

const { Title, Text } = Typography;

/**
 * Drawer showing the members screened at a single event, with a risk filter
 * (high-risk / all), multi-select and bulk email/text actions, and per-member
 * actions. Bulk actions target the checked members, or everyone in the current
 * view when nothing is checked.
 */
const EventOutreachDrawer = ({ visible, event, onClose, onCompose, reloadKey }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [riskFilter, setRiskFilter] = useState('all');

  const loadMembers = useCallback(async () => {
    if (!event?.eventKey) return;
    setLoading(true);
    try {
      const res = await caseService.getEventOutreachMembers(event.eventKey, {
        risk: riskFilter === 'all' ? 'all' : 'high'
      });
      setMembers(res.data?.members || []);
      setSelectedRowKeys([]);
    } catch (err) {
      message.error('Failed to load members for this event');
    } finally {
      setLoading(false);
    }
  }, [event?.eventKey, riskFilter]);

  useEffect(() => {
    if (visible) {
      loadMembers();
    }
  }, [visible, reloadKey, loadMembers]);

  useEffect(() => {
    if (visible) {
      setSelectedRowKeys([]);
      setRiskFilter('all');
    }
  }, [visible]);

  const selectedMembers = members.filter((m) => selectedRowKeys.includes(m.patientId));
  // Target the checked members, or all currently loaded members if none checked.
  const bulkTargets = selectedMembers.length > 0 ? selectedMembers : members;
  const bulkLabel = selectedMembers.length > 0
    ? `${selectedMembers.length} selected`
    : `all ${members.length}`;

  const handleBulk = (methodType) => {
    if (bulkTargets.length === 0) return;
    onCompose(methodType, bulkTargets, event.eventKey, event.eventName);
  };

  const contacted = members.filter((m) => m.outreach?.contacted).length;
  const opened = members.filter((m) => m.outreach?.responded).length;
  const responded = members.filter((m) => m.outreach?.replied).length;

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      width={960}
      styles={{ body: { display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      title={
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {event?.eventName || 'Event'}
          </Title>
          <Space size={16} style={{ marginTop: 4, color: COLORS.textMedium, fontSize: 13 }}>
            {event?.eventDate && (
              <span>
                <FontAwesomeIcon icon={faCalendar} /> {new Date(event.eventDate).toLocaleDateString()}
              </span>
            )}
            {event?.eventLocation && (
              <span>
                <FontAwesomeIcon icon={faLocationDot} /> {event.eventLocation}
              </span>
            )}
          </Space>
        </div>
      }
    >
      <DrawerStatsBar>
        <div className="drawer-stat">
          <div className="drawer-stat-value">{members.length}</div>
          <div className="drawer-stat-label">{riskFilter === 'all' ? 'Members Shown' : 'High-Risk Members'}</div>
        </div>
        <div className="drawer-stat">
          <div className="drawer-stat-value accent-blue">{contacted}</div>
          <div className="drawer-stat-label">Contacted</div>
        </div>
        <div className="drawer-stat">
          <div className="drawer-stat-value">{opened}</div>
          <div className="drawer-stat-label">Opened</div>
        </div>
        <div className="drawer-stat">
          <div className="drawer-stat-value accent-green">{responded}</div>
          <div className="drawer-stat-label">Responded</div>
        </div>
      </DrawerStatsBar>

      <TableToolbar>
        <Space size={12} wrap>
          <Segmented
            value={riskFilter}
            onChange={setRiskFilter}
            options={[
              { label: 'High risk', value: 'high' },
              { label: 'All members', value: 'all' }
            ]}
          />
          <Text className="toolbar-hint">
            {selectedRowKeys.length > 0
              ? `${selectedRowKeys.length} selected`
              : 'Sending to everyone shown — or check members to target a subset'}
          </Text>
        </Space>
        <Space>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faEnvelope} />}
            disabled={bulkTargets.length === 0}
            onClick={() => handleBulk('email')}
          >
            Email ({bulkLabel})
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={faCommentSms} />}
            disabled={bulkTargets.length === 0}
            onClick={() => handleBulk('text')}
          >
            Text ({bulkLabel})
          </Button>
        </Space>
      </TableToolbar>

      <OutreachMembersTable
        members={members}
        loading={loading}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        scrollY="calc(100vh - 340px)"
        onEmail={(recs) => onCompose('email', recs, event.eventKey, event.eventName)}
        onText={(recs) => onCompose('text', recs, event.eventKey, event.eventName)}
      />
    </Drawer>
  );
};

export default EventOutreachDrawer;
